import React, { useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Typography,
} from '@mui/material';

import CorporateEventsService from '~/services/CorporateEventsService';

interface DataManagementProps {
  eventId?: string;
}

export default function DataManagement({ eventId }: DataManagementProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const clearPassportMutation = useMutation({
    mutationFn: () => {
      if (!eventId) {
        throw new Error('Event ID is required');
      }
      return CorporateEventsService.clearRegistrationsPassportData(eventId);
    },
    onSuccess: () => {
      enqueueSnackbar('Passport data cleared successfully', { variant: 'success' });
      setConfirmDialogOpen(false);
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error clearing passport data: ${error.message}`, { variant: 'error' });
    },
  });

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    if (clearPassportMutation.isPending) {
      return;
    }
    setConfirmDialogOpen(false);
  };

  const handleConfirmClear = () => {
    clearPassportMutation.mutate();
  };

  if (!eventId) {
    return <Alert severity="info">Please save the event first before managing data.</Alert>;
  }

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Passport Data Management
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Once you have finished using the passport information provided by registrants, you can permanently delete this
          sensitive data to comply with data protection regulations.
        </Typography>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Important:</strong> This action will permanently delete passport numbers and expiry dates from all
          registrations for this event. This cannot be undone. Make sure you no longer need this data before proceeding.
        </Alert>

        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteForeverIcon />}
          onClick={handleOpenConfirmDialog}
          disabled={clearPassportMutation.isPending}
        >
          Clear Passport Data
        </Button>
      </Paper>

      <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Passport Data Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The deletion of this data is intended for customer data protection, therefore it cannot be reversed.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>
            Please confirm that you no longer need the passport data before proceeding with the deletion.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={clearPassportMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmClear}
            color="error"
            variant="contained"
            disabled={clearPassportMutation.isPending}
            startIcon={clearPassportMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {clearPassportMutation.isPending ? 'Clearing...' : 'Confirm Deletion'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
